<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import PropertyOffersAPI from 'dashboard/api/propertyOffers';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import LaisTimelineSeparator from 'dashboard/components-next/timeline/LaisTimelineSeparator.vue';
import { useFeatureFlag } from 'dashboard/composables/useFeatureFlag';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { buildFallbackOfferStatusPayload } from 'dashboard/helper/lais/offerPresenter';
import {
  classesForState,
  iconForStep,
  normalizeStep,
} from './offerTimelineHelper';

const props = defineProps({
  // Quando offerId é informado, o componente busca a timeline sozinho.
  offerId: { type: [Number, String], default: null },
  // Caso o pai já tenha o payload (ex.: vindo do serializer), pode injetá-lo.
  timeline: { type: Object, default: null },
  // Oferta carregada pelo detalhe. Usada para o fallback de step único.
  offer: { type: Object, default: null },
  salesFlowId: { type: [Number, String], default: null },
});
const emit = defineEmits(['errorRefetch']);

// Strings PT-BR centralizadas (o módulo LAIS usa pt_BR inline; mantê-las em JS
// evita bare strings no template e facilita futura extração para i18n).
const LABELS = {
  loading: 'Carregando andamento da proposta…',
  error: 'Ocorreu algo inesperado',
  errorDescription: 'Atualize a página para carregar as informações novamente',
  retry: 'Atualizar página',
  empty: 'Nenhum step registrado para esta proposta.',
};
const loading = ref(false);
const error = ref(false);
const internalPayload = ref(null);
const statusListPayload = ref(null);
const { isEnabled: isFullStatusListEnabled } = useFeatureFlag(
  FEATURE_FLAGS.OFFER_STATUS_LIST
);

const resolvedSalesFlowId = computed(
  () =>
    props.salesFlowId ||
    props.offer?.sales_flow_id ||
    props.offer?.salesFlowId ||
    null
);

const shouldFetchStatusList = computed(
  () =>
    Boolean(isFullStatusListEnabled.value) && Boolean(resolvedSalesFlowId.value)
);

const fallbackPayload = computed(() =>
  props.offer ? buildFallbackOfferStatusPayload(props.offer) : null
);

// Precedence, richest first: the sales-flow status list, then a payload the
// parent injected, then the timeline this component fetched, and only then the
// client-side reconstruction — which is a *fallback*, not the primary source.
// It used to come first, which made the injected payload and the
// `GET /property_offers/:id/timeline` endpoint unreachable in practice.
const payload = computed(() => {
  if (shouldFetchStatusList.value) {
    return statusListPayload.value || fallbackPayload.value;
  }

  return props.timeline || internalPayload.value || fallbackPayload.value;
});

const steps = computed(() =>
  (Array.isArray(payload.value)
    ? payload.value
    : payload.value?.steps || []
  ).map(step => normalizeStep(step))
);

const macroStatusLabel = computed(
  () =>
    payload.value?.macro_status_label || payload.value?.macroStatusLabel || ''
);

const formatDate = value => {
  if (!value) return '';
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    }).format(new Date(value));
  } catch {
    return '';
  }
};

const stepDateLabel = step => {
  if (step.state === 'cancelado' && step.canceledAt) {
    return formatDate(step.canceledAt);
  }
  if (step.completedAt) return formatDate(step.completedAt);
  if (step.startedAt) return formatDate(step.startedAt);
  return '';
};

const stepMarker = step => iconForStep(step);

const fetchTimeline = async () => {
  if (!props.offerId) return;
  loading.value = true;
  error.value = false;
  try {
    const { data } = await PropertyOffersAPI.getTimeline(props.offerId);
    internalPayload.value = data;
  } catch {
    internalPayload.value = null;
    // A reconstruction from the offer we already hold beats an error box, so
    // only surface the failure when there is nothing left to render.
    error.value = !fallbackPayload.value;
  } finally {
    loading.value = false;
  }
};

const fetchStatusList = async () => {
  if (!shouldFetchStatusList.value) return;
  loading.value = true;
  error.value = false;
  try {
    const { data } = await PropertyOffersAPI.statusList(
      resolvedSalesFlowId.value
    );
    statusListPayload.value = data;
  } catch {
    statusListPayload.value = null;
    error.value = !fallbackPayload.value;
  } finally {
    loading.value = false;
  }
};

const load = () => {
  if (shouldFetchStatusList.value) {
    fetchStatusList();
    return;
  }

  // Only the injected payload makes the request unnecessary; holding the offer
  // just means we have a fallback to fall back *to*.
  if (!props.timeline && props.offerId) fetchTimeline();
};

const retryAfterError = () => {
  emit('errorRefetch');
  load();
};

watch(() => props.offerId, load);
watch(resolvedSalesFlowId, load);
watch(isFullStatusListEnabled, load);

onMounted(() => {
  load();
});
</script>

<template>
  <div class="flex flex-col gap-3">
    <div
      v-if="macroStatusLabel"
      class="text-xs font-medium uppercase tracking-wide text-n-slate-11"
    >
      {{ macroStatusLabel }}
    </div>

    <div
      v-if="loading"
      class="flex items-center justify-center gap-2 py-6 text-sm text-n-slate-11"
    >
      <Spinner />
      <span>{{ LABELS.loading }}</span>
    </div>

    <div
      v-else-if="error"
      class="rounded-lais border border-rose-100 bg-rose-50 px-4 py-5 text-sm text-rose-800"
    >
      <div class="flex items-start gap-3">
        <span
          class="i-lucide-circle-x mt-0.5 size-5 shrink-0"
          aria-hidden="true"
        />
        <div class="flex min-w-0 flex-1 flex-col gap-1">
          <span class="font-semibold">{{ LABELS.error }}</span>
          <span class="text-xs text-rose-700">
            {{ LABELS.errorDescription }}
          </span>
          <button
            type="button"
            class="mt-2 self-start font-semibold text-rose-800 underline"
            @click="retryAfterError"
          >
            {{ LABELS.retry }}
          </button>
        </div>
      </div>
    </div>

    <div
      v-else-if="!steps.length"
      class="py-6 text-center text-sm text-n-slate-11"
    >
      {{ LABELS.empty }}
    </div>

    <ol v-else class="flex flex-col">
      <li
        v-for="(step, index) in steps"
        :key="step.key"
        class="relative flex gap-3 pb-5 last:pb-0"
      >
        <LaisTimelineSeparator
          :is-last="index >= steps.length - 1"
          :dot-class="classesForState(step.state).dot"
          :line-class="classesForState(step.state).line"
        >
          <span class="size-3.5" :class="stepMarker(step)" aria-hidden="true" />
        </LaisTimelineSeparator>

        <div class="flex min-w-0 flex-col gap-0.5 pt-0.5">
          <span
            class="break-words text-sm font-medium"
            :class="[
              classesForState(step.state).text,
              step.current ? 'font-semibold' : '',
            ]"
          >
            {{ step.label }}
          </span>
          <span
            v-if="stepDateLabel(step)"
            class="break-words text-xs text-n-slate-11"
          >
            {{ stepDateLabel(step) }}
          </span>
          <span
            v-if="step.description"
            class="break-words text-xs text-n-slate-10"
          >
            {{ step.description }}
          </span>
        </div>
      </li>
    </ol>
  </div>
</template>
